// admin(gnbs) → 통합 사이드바 메뉴 변환.
// admin 로그인 시 받은 auth_info.gnbs 를 LMS 사이드바 구조로 바꾼다.
// 라벨 매핑은 admin 프로젝트 store.js 의 menuName 을 이식.

import type { AdminGnb } from "../utils/adminAuth";

// route/group → 한글 라벨
export const adminMenuName: Record<string, string> = {
  notice: "일반공지",
  "notice-popup": "팝업 공지 관리",
  "agency-notice": "교육공지",
  "agency-active-share": "지사활동공유",
  afl: "가맹관리",
  "afl-agencies": "지사",
  "afl-headquarters": "본부 관리",
  "afl-academies": "가맹원",
  "afl-statistics": "통계",
  "payssam-merchants": "결제선생 연동 가맹원",
  "afl-pointcart": "포인트 지급 관리",
  "afl-autopoint": "교재비 입금 내역",
  "afl-academy-point": "원장 포인트 충전 내역",
  "afl-join-fee": "가맹비 입금 내역",
  "afl-agency-point": "지사 예치금 입금 내역",
  "payssam-payments": "결제선생 결제 내역",
  inquiry: "가맹 문의 관리",
  "franchise-inquiry": "가맹문의",
  "class-inquiry": "수업문의",
  "franchise-inquiry-stats": "전환율 현황",
  "agency-region": "지역-지사 배정",
  event: "행사관리",
  "event-zoom": "본사 미팅관리",
  "event-manage": "행사관리",
  "event-edu": "교육관리",
  "event-private": "사설관리",
  "event-academy": "학설관리",
  "event-etc": "기타행사",
  member: "회원",
  "member-members": "회원",
  "member-parent": "학부모",
  "member-billing-send": "원비 청구서 발송",
  "member-billing-status": "발송 & 수납 내역",
  "member-billing-statistics": "수납내역 통계",
  "member-counseling-management": "상담관리",
  "member-cash-receipt": "현금영수증 발행",
  "member-textbook-invoice": "교재비 청구서 발송",
  "member-textbook-invoice-agency": "교재비 청구서 발송(지사)",
  "member-agency-payssam": "지사용 결제선생 연동",
  "member-sleep": "휴회원관리",
  "member-parentnoti": "학부모 알림톡",
  product: "교재주문",
  "aca-pointcharge": "포인트 내역",
  "agency-pointcharge": "지사 예치금 내역",
  "agency-textbook-deposit": "교재비 입금 내역",
  "agency-academy-charge": "원장 포인트 충전 내역",
  "agency-order-approval": "교재주문 승인",
  "product-order": "교재 주문",
  "product-order-v2": "교재주문(신)",
  "book-ownership": "교재 소유권 관리",
  "book-return-records": "본사 반품 기록",
  "book-hq-returns": "본사 반품 승인",
  "book-market": "교재 장터",
  "voucher-order": "이용권 구매",
  "od-delivery": "배송",
  "share-board": "공유 게시판",
  "share-board-agen": "지사 모임 자료실",
  "share-board-aca": "원장 공유 게시판",
  aca: "클래스 관리",
  "aca-info": "내정보",
  "aca-teacher": "교사",
  "aca-class": "클래스",
  attendance: "출결관리",
  "attendance-send": "출결톡보내기",
  "attendance-list": "출결톡내역",
  "attendance-monthly-list": "출결월간리스트",
  "agency-info": "내정보",
  contents: "컨텐츠",
  "contents-product": "교재추가",
  "contents-setting": "교재관리",
  "contents-stock": "재고관리",
  od: "주문/배송",
  "od-partners": "협력업체",
  "calculate-group": "정산관리",
  "point-group": "포인트 관리",
  calculate: "정산",
  "calculate-v2": "정산(신규)",
  "calculate-fee-rules": "교재 수수료 요율(구)",
  "calculate-fee-rules-v2": "교재 수수료 요율(신규)",
  "calculate-grades-v2": "등급 관리",
  material: "자료실",
  materials: "전체",
  qna: "소통광장",
  "qna-FAQ": "자주묻는질문",
  "qna-mtm": "1:1문의",
  "qna-proposal": "교육 노하우 제안",
  "qna-help": "도움말",
  "agency-proposal-board": "지사건의 게시판",
  "chat-inquiry": "채팅 문의",
};

// 최상위 admin 메뉴(gnb) 아이콘 — 그룹/단일 항목의 id(=group 또는 route) 기준.
// 게시판 계열(notice/agency-notice/material/share-board/qna/agency-active-share)은
// boards.ts 의 아이콘과 동일하게 맞춰 사이드바-본문 정체성을 통일한다.
export const adminMenuIcon: Record<string, string> = {
  notice: "📢", // 일반공지
  "notice-popup": "🔔", // 팝업 공지 관리
  "agency-notice": "🎓", // 교육공지
  event: "🎪", // 행사관리
  "calculate-group": "🧮", // 정산 관리
  "point-group": "💰", // 포인트 관리
  afl: "🏢", // 가맹관리
  inquiry: "📨", // 문의관리 (가맹문의·수업문의)
  member: "👥", // 회원
  product: "🛒", // 교재주문
  contents: "📦", // 컨텐츠
  od: "🚚", // 주문/배송
  "od-delivery": "🚚", // 배송
  "aca-class": "🏫", // 클래스 (원장·교사)
  attendance: "🗓️", // 출결관리
  material: "📁", // 자료실
  "share-board": "🤝", // 공유 게시판
  qna: "💬", // 소통광장
  "agency-active-share": "📣", // 지사활동공유
  "agency-info": "ℹ️", // 내정보(지사)
  "aca-info": "ℹ️", // 내정보(학원)
  "chat-inquiry": "🗨️", // 채팅 문의
};

export function adminIcon(id?: string): string {
  return (id && adminMenuIcon[id]) || "";
}

export interface AdminMenuItem {
  id: string;
  title: string;
  route?: string; // admin 라우트 (딥링크/이관 대상)
  children?: AdminMenuItem[];
}

function label(key: string): string {
  return adminMenuName[key] || key;
}

// LMS 앱 안으로 이관 완료된 admin 화면 (admin route → LMS 내부 경로)
// 여기 등록된 화면은 SSO 딥링크 대신 LMS 내부 라우팅으로 연결된다.
export const migratedRoutes: Record<string, string> = {
  notice: "/admin/notice",
  "notice-popup": "/admin/notice-popup",
  "agency-notice": "/admin/agency-notice",
  "event-zoom": "/admin/event-zoom",
  "event-manage": "/admin/event-manage",
};

export function migratedPath(route?: string): string | null {
  if (!route) return null;
  // 공유 게시판 (지사/원장) — 단일 동적 라우트
  if (route === "share-board-agen" || route === "share-board-aca") {
    return `/admin/share-board/${route}`;
  }
  // 소통광장 (자주묻는질문/교육노하우제안/지사건의)
  if (route === "qna-FAQ" || route === "qna-proposal" || route === "agency-proposal-board") {
    return `/admin/qna/${route}`;
  }
  // 지사활동공유
  if (route === "agency-active-share") return "/admin/agency-active-share";
  // 컨텐츠 > 교재관리 / 재고관리
  if (route === "contents-setting") return "/admin/contents-setting";
  if (route === "contents-stock") return "/admin/contents-stock";
  if (route === "contents-product") return "/admin/contents-product";
  // 정산 관리 > 정산 / 수수료 현황
  if (route === "calculate") return "/admin/calculate";
  if (route === "calculate-v2") return "/admin/calculate-v2";
  if (route === "calculate-fee-rules") return "/admin/calculate-fee-rules";
  if (route === "calculate-fee-rules-v2") return "/admin/calculate-fee-rules-v2";
  if (route === "calculate-grades-v2") return "/admin/calculate-grades-v2";
  // 클래스 관리 > 교사 / 클래스
  if (route === "aca-teacher") return "/admin/aca-teacher";
  if (route === "aca-class") return "/admin/aca-class";
  if (route === "aca-info") return "/admin/aca-info";
  if (route === "agency-info") return "/admin/agency-info";
  if (route === "chat-inquiry") return "/admin/chat-inquiry";
  // 문의관리 > 가맹문의 / 수업문의
  if (route === "franchise-inquiry") return "/admin/franchise-inquiry";
  if (route === "class-inquiry") return "/admin/class-inquiry";
  if (route === "franchise-inquiry-stats") return "/admin/franchise-inquiry-stats";
  if (route === "agency-region") return "/admin/agency-region";
  // 가맹관리 > 결제선생 연동 가맹원 / 결제 내역 (본사 전용 읽기)
  if (route === "payssam-merchants") return "/admin/payssam-merchants";
  if (route === "payssam-payments") return "/admin/payssam-payments";
  // 출결관리 > 출결톡보내기 / 출결톡내역 / 출결월간리스트
  if (route === "attendance-send") return "/admin/attendance-send";
  if (route === "attendance-list") return "/admin/attendance-list";
  if (route === "attendance-monthly-list") return "/admin/attendance-monthly-list";
  // 가맹관리 > 지사 / 가맹원 / 본부 관리 (읽기)
  if (route === "afl-agencies") return "/admin/afl-agencies";
  if (route === "afl-academies") return "/admin/afl-academies";
  if (route === "afl-headquarters") return "/admin/afl-headquarters";
  if (route === "afl-pointcart") return "/admin/afl-pointcart";
  if (route === "afl-statistics") return "/admin/afl-statistics";
  // 가맹관리 > 입금내역 2종 (교재비/가맹비)
  if (route === "afl-autopoint") return "/admin/afl-autopoint";
  if (route === "afl-academy-point") return "/admin/afl-academy-point";
  if (route === "afl-join-fee") return "/admin/afl-join-fee";
  if (route === "afl-agency-point") return "/admin/afl-agency-point";
  // 회원 그룹 Phase1 (읽기): 회원 / 학부모 / 휴회원관리
  if (route === "member-members") return "/admin/member-members";
  if (route === "member-parent") return "/admin/member-parent";
  if (route === "member-sleep") return "/admin/member-sleep";
  if (route === "member-counseling-management") return "/admin/member-counseling-management";
  if (route === "member-parentnoti") return "/admin/member-parentnoti";
  if (route === "member-billing-status") return "/admin/member-billing-status";
  if (route === "member-cash-receipt") return "/admin/member-cash-receipt";
  if (route === "member-agency-payssam") return "/admin/member-agency-payssam";
  if (route === "member-billing-send") return "/admin/member-billing-send";
  if (route === "member-textbook-invoice") return "/admin/member-textbook-invoice";
  if (route === "member-textbook-invoice-agency") return "/admin/member-textbook-invoice-agency";
  // 교재주문 > 포인트충전 / 교재 주문 / 이용권 구매 (조회+장바구니)
  if (route === "aca-pointcharge") return "/admin/aca-pointcharge";
  if (route === "agency-pointcharge") return "/admin/agency-pointcharge";
  if (route === "agency-textbook-deposit") return "/admin/agency-textbook-deposit";
  if (route === "agency-academy-charge") return "/admin/agency-academy-charge";
  if (route === "agency-order-approval") return "/admin/agency-order-approval";
  if (route === "product-order") return "/admin/product-order";
  if (route === "product-order-v2") return "/admin/product-order-v2";
  if (route === "book-ownership") return "/admin/book-ownership";
  if (route === "book-return-records") return "/admin/book-return-records";
  if (route === "book-hq-returns") return "/admin/book-hq-returns";
  if (route === "book-market") return "/admin/book-market";
  if (route === "voucher-order") return "/admin/voucher-order";
  // 주문/배송 > 배송
  if (route === "od-delivery") return "/admin/od-delivery";
  // 자료실 관리 화면 (게시판이 아니라 menus 관리)
  if (route === "material-manage") return "/admin/material-manage";
  // 자료실은 게시판이 DB(menus)로 동적 관리되므로 단일 동적 라우트로 매핑
  if (route === "materials" || route.startsWith("material-")) {
    return `/admin/material/${route}`;
  }
  return migratedRoutes[route] ?? null;
}

// 사이드바에서 숨길 route (소통광장: 자주묻는질문·교육 노하우 제안)
const HIDDEN_MENU_ROUTES = new Set<string>(["qna-FAQ", "qna-proposal"]);

/** gnbs → 통합 사이드바용 admin 메뉴 그룹 배열 */
export function buildAdminMenu(gnbs: AdminGnb[] | undefined | null): AdminMenuItem[] {
  if (!Array.isArray(gnbs)) return [];
  return gnbs
    .map((g): AdminMenuItem | null => {
      if (Array.isArray(g.route)) {
        const children = g.route
          .filter((item) => !HIDDEN_MENU_ROUTES.has(item.route))
          .map((item) => ({
            id: item.route,
            title: item.name || label(item.route),
            route: item.route,
          }));
        // 교재주문(신)·교재 소유권 관리 병행 주입 — 백엔드 gnbs 수정 없이 '교재 주문' 바로 뒤에 프론트 항목 추가.
        const poIdx = children.findIndex((c) => c.route === "product-order");
        if (poIdx >= 0 && !children.some((c) => c.route === "product-order-v2")) {
          // 교재 장터(book-market)는 교재주문(신) 화면의 탭으로 편입 → 별도 메뉴 항목 미노출(학원/본사).
          children.splice(poIdx + 1, 0,
            { id: "product-order-v2", title: label("product-order-v2"), route: "product-order-v2" },
            { id: "book-ownership", title: label("book-ownership"), route: "book-ownership" },
            { id: "book-return-records", title: label("book-return-records"), route: "book-return-records" });
        }
        // 지사: '교재주문 승인' 바로 뒤에 '교재 소유권 관리'(본사 반품 등 지사 실행) + '본사 반품 기록' 주입.
        const aoaIdx = children.findIndex((c) => c.route === "agency-order-approval");
        if (aoaIdx >= 0 && !children.some((c) => c.route === "book-ownership")) {
          children.splice(aoaIdx + 1, 0,
            { id: "book-ownership", title: label("book-ownership"), route: "book-ownership" },
            { id: "book-market", title: label("book-market"), route: "book-market" },
            { id: "book-return-records", title: label("book-return-records"), route: "book-return-records" });
        }
        // 본사(root): 교재주문 사이드바가 없어 해당 화면 노출 자리가 없음 →
        //   컨텐츠 그룹 하위에 '본사 반품 승인'(지사 요청 본사반품 승인 특화 화면) + '본사 반품 기록' 주입.
        //   (교재 소유권 관리는 본사에 미노출 — 지사 본사반품 승인은 전용 화면으로 대체)
        if (g.group === "contents" && !children.some((c) => c.route === "book-hq-returns")) {
          children.push(
            { id: "book-hq-returns", title: label("book-hq-returns"), route: "book-hq-returns" },
            { id: "book-return-records", title: label("book-return-records"), route: "book-return-records" });
        }
        if (children.length === 0) return null; // 하위가 전부 숨김이면 그룹 제거
        // 자식이 하나뿐인 그룹은 토글 대신 단일 항목으로(그룹 라벨·아이콘 유지, 그 게시판으로 바로 이동)
        if (children.length === 1) {
          return { id: g.group || children[0].id, title: label(g.group || "") || children[0].title, route: children[0].route };
        }
        // 그룹 (하위 메뉴 보유)
        return {
          id: g.group || "",
          title: label(g.group || ""),
          children,
        };
      }
      // 단일 항목 (route 미지정 방어)
      const route = g.route ?? "";
      if (HIDDEN_MENU_ROUTES.has(route)) return null;
      return {
        id: route,
        title: label(route),
        route,
      };
    })
    .filter((x): x is AdminMenuItem => x !== null);
}
